import { useMemo, useState } from 'react'
import type { AgentOutputProps } from '../types'

/**
 * DiffRenderer — latex-debugger, office-debugger.
 *
 * Expects { changes: [{ line, error_type?, fix }], fixed_source? | fixed_script? }.
 * Shows the line-numbered change list on the left + an optional "View fixed
 * source" toggle that reveals the patched code below.
 */

interface Change {
  line?: number
  error_type?: string
  fix: string
  description?: string
}

interface DiffPayload {
  changes?: Change[]
  fixed_source?: string
  fixed_script?: string
  language?: string
}

function pick(result: any): DiffPayload | null {
  if (!result) return null
  const r = result
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    if (src && (Array.isArray(src.changes) || src.fixed_source || src.fixed_script)) {
      return src as DiffPayload
    }
  }
  return null
}

export default function DiffRenderer(props: AgentOutputProps) {
  const diff = useMemo(() => pick(props.result), [props.result])
  const [showSource, setShowSource] = useState(false)

  if (!diff) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No diff payload found in the agent result.
      </div>
    )
  }

  const source = diff.fixed_source || diff.fixed_script
  const changes = diff.changes || []

  return (
    <div className="space-y-3">
      {changes.length > 0 ? (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-muted/40 border-b border-border text-xs uppercase tracking-wide text-muted-foreground font-semibold">
            {changes.length} change{changes.length === 1 ? '' : 's'}
          </div>
          {changes.map((c, i) => (
            <div key={i} className="px-4 py-3 border-b border-border/60 last:border-0">
              <div className="flex items-center gap-3 mb-1">
                {typeof c.line === 'number' && (
                  <span className="text-xs font-mono bg-primary/20 text-primary px-2 py-0.5 rounded">
                    line {c.line}
                  </span>
                )}
                {c.error_type && (
                  <span className="text-xs font-mono text-muted-foreground">{c.error_type}</span>
                )}
              </div>
              <div className="text-sm text-foreground whitespace-pre-wrap font-mono">{c.fix}</div>
              {c.description && (
                <div className="text-xs text-muted-foreground mt-1">{c.description}</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground italic">No change list present.</div>
      )}

      {source && (
        <div>
          <button
            onClick={() => setShowSource((v) => !v)}
            className="text-xs underline text-primary"
          >
            {showSource ? 'Hide fixed source' : 'View fixed source'}
          </button>
          {showSource && (
            <pre className="mt-2 text-xs bg-muted/40 border border-border rounded p-3 overflow-auto max-h-[480px] font-mono">
              {source}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
